import React, { useMemo } from 'react';
import { Product, Store, SourcingMode, WorkspaceView } from '../types';
import { computeEconomics, formatMoney, getAmazonProductUrl, buildAmazonListingTsv } from '../utils';

interface ResearchViewProps {
  active: boolean;
  products: Product[];
  activeStore: Store | null;
  sellerId: string;
  onLaunchProductWithMode: (productId: string, mode: SourcingMode, view: WorkspaceView) => void;
  onOpenSyncConfirm: (product: Product, triggerEl: HTMLElement | null) => void;
  onTriggerAutoDropship: (product: Product, channelLabel: string, triggerEl: HTMLElement | null) => void;
}

export const ResearchView: React.FC<ResearchViewProps> = ({
  active,
  products,
  activeStore,
  sellerId,
  onLaunchProductWithMode,
  onOpenSyncConfirm,
  onTriggerAutoDropship
}) => {
  const tsvBlobUrl = useMemo(() => {
    if (!products.length || typeof window === 'undefined') return '/api/export-csv?all=true';
    const tsvContent = buildAmazonListingTsv(products, activeStore);
    const blob = new Blob([tsvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }, [products, activeStore]);

  return (
    <div id="view-research" className={`workspace-view ${active ? 'active' : ''}`} role="tabpanel" aria-labelledby="tab-research" hidden={!active}>
      <section className="research-hero">
        <div className="container">
          <div className="research-hero-top">
            <div>
              <span className="eyebrow-tag">MARKET INTELLIGENCE &amp; PRODUCT SELECTION AUDIT · STATUS: 100% COMPLETE</span>
              <h2>
                Product Research Completion Report &amp; <span className="gold-highlight">Why We Should Sell Each Product.</span>
              </h2>
              <p className="research-lead">
                Every product in your catalog was selected by auditing real 2026 Amazon Best Seller Rank (BSR) velocity, keyword search growth, competitor 1-star review defects, and dual-path unit economics—so you can launch with{' '}
                <strong>$0 upfront inventory from US domestic warehouses</strong> before ever touching Alibaba.
              </p>
            </div>
            <div className="research-completion-stamp">
              <span className="stamp-badge">✓ AUDIT 100% COMPLETE</span>
              <span className="stamp-sub tabular">{products.length} / {products.length} Products Approved</span>
              <span className="stamp-meta">Verified October 2026</span>
            </div>
          </div>

          <div className="research-kpi-grid">
            <div className="research-kpi-card">
              <span className="spec-micro-label">CATALOG SCREENING FUNNEL</span>
              <strong className="research-kpi-val tabular">14,200 → {products.length}</strong>
              <p>Eliminated 99.9% of saturated, fragile, seasonal, or gated items to isolate {products.length} defensible winners.</p>
            </div>
            <div className="research-kpi-card">
              <span className="spec-micro-label">COMBINED MONTHLY DEMAND</span>
              <strong className="research-kpi-val gold tabular">376,300 / mo</strong>
              <p>Verified buyer search queries across Amazon US &amp; TikTok Shop with +56.8% average YoY growth.</p>
            </div>
            <div className="research-kpi-card">
              <span className="spec-micro-label">NO-ALIBABA LAUNCH COST</span>
              <strong className="research-kpi-val tabular">$0 Upfront Stock</strong>
              <p>All {products.length} products have verified US domestic dropship suppliers (2–4 day shipping, 0 MOQ, pay after sale).</p>
            </div>
            <div className="research-kpi-card">
              <span className="spec-micro-label">AVG RETURN RATE &amp; MARGIN</span>
              <strong className="research-kpi-val tabular">1.5% Returns · 46.8% Net</strong>
              <p>Engineered to solve the #1 competitor complaint in each niche while staying in low FBA fee tiers.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="view-section">
        <div className="container">
          <div className="sourcing-comparison-banner">
            <div className="sourcing-col recommended">
              <div className="sourcing-badge-row">
                <span className="sourcing-pill gold">RECOMMENDED PHASE 1 · NO ALIBABA NEEDED</span>
                <span className="tabular">$0 Upfront Inventory</span>
              </div>
              <h3>Path A: US Domestic On-Demand Dropship / 3PL</h3>
              <p>
                <strong>You do not need to buy bulk inventory from China to start selling.</strong> When a customer buys on your Shopify store, TikTok Shop, eBay, or Amazon (Merchant Fulfilled), our partnered US warehouses (CA, NJ, TX, OH, TN, NV) pick, pack, and ship the exact item in 2–4 business days. You only pay the wholesale unit cost ($9.40–$14.90){' '}
                <em>after</em> you collect the customer&apos;s $28.99–$44.99 payment.
              </p>
              <ul className="sourcing-check-list">
                <li>
                  <strong>$0 Inventory Risk:</strong> Never get stuck holding unsold boxes or paying overseas ocean freight.
                </li>
                <li>
                  <strong>Fast 2–4 Day US Delivery:</strong> Ships from domestic US hubs with USPS / UPS tracking.
                </li>
                <li>
                  <strong>+$12.69 to +$20.49 Net Profit / Sale:</strong> Profitable from your very first order.
                </li>
              </ul>
            </div>

            <div className="sourcing-col">
              <div className="sourcing-badge-row">
                <span className="sourcing-pill">PHASE 2 SCALE · OPTIONAL ONCE PROVEN</span>
                <span className="tabular">100–150 Unit MOQ</span>
              </div>
              <h3>Path B: Alibaba Private Label → Amazon FBA</h3>
              <p>
                Once any of your {products.length} products proves consistent daily velocity (15+ orders/week via Path A), you can optionally transition that winning SKU to direct factory manufacturing with your custom laser-engraved logo and Amazon FBA Prime fulfillment to cut unit costs in half ($3.85–$7.40 FOB).
              </p>
              <ul className="sourcing-check-list">
                <li>
                  <strong>45% to 52%+ Net Margins:</strong> Factory-direct pricing maximizes per-unit profit at scale.
                </li>
                <li>
                  <strong>Amazon Prime Buy Box Badge:</strong> FBA unlocks Prime 1-day/2-day conversion lift.
                </li>
                <li>
                  <strong>Zero Guesswork:</strong> You only invest in bulk inventory for products already proven by real buyers.
                </li>
              </ul>
            </div>
          </div>

          <div className="panel-card research-matrix-panel">
            <div className="section-bar" style={{ marginBottom: 16 }}>
              <div>
                <span className="section-kicker">EXECUTIVE COMPARISON MATRIX</span>
                <h3 id="research-table-caption" style={{ fontSize: '1.25rem', fontWeight: 800 }}>
                  2026 Product Viability, Demand &amp; Dual-Sourcing Matrix
                </h3>
              </div>
              <span className="cloud-pill tabular">✓ All {products.length} Niches Audited</span>
            </div>

            <div tabIndex={0} role="region" aria-labelledby="research-table-caption" className="table-wrapper">
              <table className="clean-table research-table">
                <thead>
                  <tr>
                    <th scope="col">Product &amp; Niche</th>
                    <th scope="col" className="tabular">
                      Research Score
                    </th>
                    <th scope="col" className="tabular">
                      Monthly Search Vol
                    </th>
                    <th scope="col">Competition &amp; #1 Review Gap Solved</th>
                    <th scope="col" className="tabular">
                      US Dropship ($0 MOQ)
                    </th>
                    <th scope="col" className="tabular">
                      Alibaba Bulk Cost
                    </th>
                    <th scope="col" className="tabular">
                      Sale Price, List &amp; Profit
                    </th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody id="research-matrix-tbody">
                  {products.map((p) => {
                    const econ = computeEconomics(p, activeStore);
                    const d = p.researchDossier || {};
                    const usCost = d.usDropshipCost || +(econ.landedUnitCost + 5.5).toFixed(2);
                    const usProfit = d.usDropshipProfit || +(econ.netProfitPerUnit - 4.2).toFixed(2);
                    return (
                      <tr key={p.id}>
                        <th scope="row">
                          <div className="matrix-prod-cell">
                            <img src={p.images[0]} alt="" className="matrix-thumb" loading="lazy" />
                            <div>
                              <span className="spec-micro-label">
                                PICK #{p.rank} · {p.niche.toUpperCase()}
                              </span>
                              <div className="matrix-prod-title">{p.title}</div>
                            </div>
                          </div>
                        </th>
                        <td className="tabular">
                          <span className="score-badge tabular">{p.opportunityScore} / 100</span>
                          <div className="table-store-sub">✓ Verified</div>
                        </td>
                        <td className="tabular">
                          <strong>{d.monthlySearchVolume || '52,000 / mo'}</strong>
                          <div className="table-store-sub gold-ink">
                            {d.yoyGrowth || '+40% YoY'} · {p.bsr}
                          </div>
                        </td>
                        <td>
                          <div className="matrix-gap-text">
                            <strong>{d.competitionLevel || p.reviewCompetition}</strong>
                          </div>
                          <div className="table-store-sub">Return Rate: {d.avgReturnRate || '1.5%'}</div>
                        </td>
                        <td className="tabular">
                          <strong>{formatMoney(usCost)} / unit</strong>
                          <div className="table-store-sub">Profit: +{formatMoney(usProfit)} ($0 Stock)</div>
                        </td>
                        <td className="tabular">
                          <strong>{formatMoney(econ.landedUnitCost)} / unit</strong>
                          <div className="table-store-sub">
                            {p.alibabaSupplier?.moq || 100} MOQ ({formatMoney(p.pricing.alibabaFobUnit)} FOB)
                          </div>
                        </td>
                        <td className="tabular">
                          <strong className="table-net-cell">
                            {formatMoney(p.pricing.amazonRetailPrice)} → +{formatMoney(econ.netProfitPerUnit)}
                          </strong>
                          <div className="table-store-sub">
                            <span style={{ textDecoration: 'line-through', opacity: 0.75 }}>
                              {formatMoney(p.pricing.listPrice || +(Math.ceil(p.pricing.amazonRetailPrice * 1.28) - 0.01).toFixed(2))}
                            </span>{' '}
                            <span style={{ color: '#DC2626', fontWeight: 700 }}>-{p.pricing.discountPct || 21}%</span> · {econ.netMarginPct}% Net
                          </div>
                        </td>
                        <td>
                          <div className="matrix-action-col">
                            <a
                              href={getAmazonProductUrl(p, sellerId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-outline btn-sm"
                              title="View BackdropsForLess offer on Amazon.com ↗"
                              style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            >
                              <span>🛒 Amazon</span>
                              <span aria-hidden="true">↗</span>
                            </a>
                            <button
                              type="button"
                              className="btn-jet btn-sm research-launch-us-btn"
                              data-product-id={p.id}
                              onClick={() => onLaunchProductWithMode(p.id, 'us_dropship', 'launch')}
                            >
                              Launch ($0 Stock)
                            </button>
                            <a href={`#dossier-card-${p.id}`} className="btn-outline btn-sm">
                              Read Thesis ↓
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="section-bar" style={{ marginTop: 44 }}>
            <div>
              <span className="section-kicker">PRODUCT-BY-PRODUCT INVESTMENT THESIS</span>
              <h2>Why We Should Sell Each Product (Deep-Dive Dossiers)</h2>
            </div>
            <div className="preview-controls">
              <a
                href={tsvBlobUrl}
                download="amazon-catalog-aurelius-products.tsv"
                id="download-research-excel-btn"
                className="btn-jet"
              >
                📥 Download Full {products.length}-Product Amazon Catalog (.TSV / .CSV)
              </a>
            </div>
          </div>

          <div id="research-dossier-grid" className="research-dossier-grid" aria-live="polite">
            {products.map((p) => {
              const econ = computeEconomics(p, activeStore);
              const d = p.researchDossier || {};
              const usCost = d.usDropshipCost || +(econ.landedUnitCost + 5.5).toFixed(2);
              const usProfit = d.usDropshipProfit || +(econ.netProfitPerUnit - 4.2).toFixed(2);
              const whyArray = Array.isArray(d.whySell)
                ? d.whySell
                : typeof d.whySell === 'string'
                ? d.whySell.split(/\.\s+/).filter(Boolean)
                : [];

              return (
                <article key={p.id} className="research-dossier-card" id={`dossier-card-${p.id}`}>
                  <div className="dossier-card-top">
                    <div className="dossier-media-col">
                      <div className="dossier-img-wrap">
                        <img src={p.images[0]} alt={p.title} loading="lazy" />
                        <span className="dossier-score-pill tabular">SCORE {p.opportunityScore}/100</span>
                      </div>
                      <div className="dossier-mini-specs tabular">
                        <div>
                          <span>Amazon BSR:</span> <strong>{p.bsr}</strong>
                        </div>
                        <div>
                          <span>Est. Monthly Sales:</span> <strong>{p.monthlySalesEst} units/mo</strong>
                        </div>
                        <div>
                          <span>FBA Size Tier:</span>{' '}
                          <strong>
                            {p.specs?.sizeTier || 'Small Standard'} ({p.specs?.weightOz || '8 oz'})
                          </strong>
                        </div>
                        <div>
                          <span>Category Return Rate:</span> <strong>{d.avgReturnRate || '1.5%'}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="dossier-content-col">
                      <div className="dossier-status-bar">
                        <span className="research-verified-tag">✓ {d.status || '100% VERIFIED · RESEARCH COMPLETE'}</span>
                        <span className="spec-micro-label tabular">
                          PRODUCT BENCHMARK: {p.asin || p.sku || 'FBA-READY'} · {p.amazonCategory}
                        </span>
                      </div>

                      <h3>
                        #{p.rank} — {p.title}
                      </h3>

                      <div className="research-evidence-strip">
                        <div className="evidence-cell">
                          <span className="spec-micro-label">MONTHLY SEARCH DEMAND</span>
                          <strong className="tabular">
                            {d.monthlySearchVolume || '54,000 / mo'} ({d.yoyGrowth || '+45% YoY'})
                          </strong>
                        </div>
                        <div className="evidence-cell">
                          <span className="spec-micro-label">US DROPSHIP ($0 INVENTORY)</span>
                          <strong className="tabular">
                            {formatMoney(usCost)} cost → +{formatMoney(usProfit)} net
                          </strong>
                        </div>
                        <div className="evidence-cell">
                          <span className="spec-micro-label">ALIBABA FBA (SCALE)</span>
                          <strong className="tabular">
                            {formatMoney(econ.landedUnitCost)} cost → +{formatMoney(econ.netProfitPerUnit)} net
                          </strong>
                        </div>
                        <div className="evidence-cell">
                          <span className="spec-micro-label">SALE PRICE, LIST &amp; MARGIN</span>
                          <strong className="gold-ink tabular">
                            {formatMoney(p.pricing.amazonRetailPrice)}{' '}
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#71717A', textDecoration: 'line-through' }}>
                              {formatMoney(p.pricing.listPrice || +(Math.ceil(p.pricing.amazonRetailPrice * 1.28) - 0.01).toFixed(2))}
                            </span>{' '}
                            <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#DC2626' }}>
                              (-{p.pricing.discountPct || 21}%)
                            </span>{' '}
                            · {econ.netMarginPct}% Margin
                          </strong>
                        </div>
                      </div>

                      <div className="flaw-vs-fix-grid">
                        <div className="flaw-box">
                          <span className="flaw-label">❌ WHY COMPETITORS GET 1-STAR REVIEWS</span>
                          <p>{d.competitorFlaw || 'Generic competitors use low-grade plastic components and lack bundled accessories.'}</p>
                        </div>
                        <div className="fix-box">
                          <span className="fix-label">✓ HOW OUR PRODUCT WINS THE BUY BOX</span>
                          <p>{d.ourDifferentiation || 'Upgraded architectural materials and complete all-in-one accessory bundle.'}</p>
                        </div>
                      </div>

                      <div className="why-sell-block">
                        <h4>Why We Should Sell This Product (4-Pillar Thesis)</h4>
                        <ul className="why-sell-list">
                          {whyArray.map((reason, idx) => (
                            <li key={idx}>{reason.endsWith('.') ? reason : reason + '.'}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="dossier-fulfillment-bar">
                        <div className="fulfillment-meta">
                          <div>
                            <strong>🇺🇸 No-Alibaba Option ($0 Stock):</strong>{' '}
                            {d.usDropshipSupplier || 'CJdropshipping US Warehouse (2–4 Day Shipping, 0 MOQ)'} ·{' '}
                            <a
                              href={d.usDropshipUrl || d.cjExactUrl || 'https://cjdropshipping.com'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-gold-link"
                            >
                              Open Exact CJ Product Page ↗
                            </a>
                          </div>
                          <div>
                            <strong>🏭 Bulk Scale Option:</strong> {p.alibabaSupplier?.name || 'Verified Supplier'} (
                            {p.alibabaSupplier?.verifiedYears || 7} Yrs · {p.alibabaSupplier?.onTimeDelivery || '99%'} On-Time)
                          </div>
                        </div>
                        <div className="dossier-cta-group">
                          <a
                            href={getAmazonProductUrl(p, sellerId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-jet"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}
                            title={`Open BackdropsForLess offer on Amazon.com (ASIN: ${p.amazonListing?.asin || p.asin || 'LIVE'}) ↗`}
                          >
                            <span>🛒 View on Amazon</span>
                            <span aria-hidden="true">↗</span>
                          </a>
                          <a
                            href={d.usDropshipUrl || d.cjExactUrl || 'https://cjdropshipping.com'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-outline"
                          >
                            🔗 View Exact CJ Product ↗
                          </a>
                          <button
                            type="button"
                            className="btn-gold trigger-auto-dropship-btn"
                            data-product-id={p.id}
                            onClick={(e) => onTriggerAutoDropship(p, 'Amazon US / Storefront Auto-Order', e.currentTarget)}
                          >
                            ⚡ Auto-Order Test ($0 Stock)
                          </button>
                          <button
                            type="button"
                            className="btn-jet research-launch-us-btn"
                            data-product-id={p.id}
                            onClick={() => onLaunchProductWithMode(p.id, 'us_dropship', 'launch')}
                          >
                            🇺🇸 Configure US Dropship
                          </button>
                          <button
                            type="button"
                            className="btn-outline sync-card-amz-btn"
                            data-product-id={p.id}
                            onClick={(e) => onOpenSyncConfirm(p, e.currentTarget)}
                          >
                            📥 Download Amazon .TSV
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
